import type { APNG } from 'apng-js';
import parseAPNG from 'apng-js';
import type Player from 'apng-js/types/library/player';
import { Box } from '@chakra-ui/react';
import type { RefObject } from 'react';

const apngBuffer = new Map<string, ArrayBuffer>();

export type ApngPlayerRef = {
  apng: APNG;
  player: Player;
  isPlaying: boolean;
  setPlaybackRate: (rate: number) => void;
};
export interface ApngPlayerProps {
  src?: string;
  autoPlay?: boolean;
  loop?: boolean;
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  playbackRate?: number;
  ref?: RefObject<ApngPlayerRef | null>;
  children?: React.ReactNode;
  onLoad?: (apng: APNG, player: Player) => void;
  onError?: (error: any) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
  onEnd?: () => void;
}

export const ApngPlayer = ({
  src = '/media/voice.apng',
  autoPlay = true,
  loop = true,
  width,
  height,
  className = '',
  style = {},
  playbackRate = 1,
  ref,
  children,
  onLoad,
  onError,
  onPlay,
  onPause,
  onStop,
  onEnd,
}: ApngPlayerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const apngRef = useRef<APNG>(null);
  const playerRef = useRef<Player>(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      apng: apngRef.current!,
      player: playerRef.current!,
      isPlaying,
      setPlaybackRate: (rate: number) => (playerRef.current!.playbackRate = rate),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoaded]
  );

  const loadAPNG = useCallback(async () => {
    if (!src) return Promise.reject(new Error('APNG src not found'));

    try {
      setIsLoaded(false);

      if (!apngBuffer.has(src)) {
        const response = await fetch(src);
        const buffer = await response.arrayBuffer();
        apngBuffer.set(src, buffer);
      }

      const apng = parseAPNG(apngBuffer.get(src)!);

      if (apng instanceof Error) {
        throw apng;
      }
      apngRef.current = apng;
      await apng.createImages();

      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = width || apng.width;
        canvas.height = height || apng.height;
      } else {
        return Promise.reject(new Error('Canvas not found'));
      }

      const player = await apng.getPlayer(canvas.getContext('2d')!);
      playerRef.current = player;
      player.playbackRate = playbackRate;

      const originalEmit = player.emit;
      player.emit = (event: any, ...args: any) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        originalEmit.call(player, event, ...args);

        switch (event) {
          case 'play':
            setIsPlaying(true);
            if (onPlay) onPlay();
            break;
          case 'pause':
            setIsPlaying(false);
            if (onPause) onPause();
            break;
          case 'stop':
            setIsPlaying(false);
            if (onStop) onStop();
            break;
          case 'end':
            setIsPlaying(false);
            if (onEnd) onEnd();
            if (loop) {
              player.play();
            }
            break;
          default:
            break;
        }
        return true;
      };

      setIsLoaded(true);
      if (onLoad) onLoad(apng, player);

      if (autoPlay) {
        player.play();
      }
    } catch (err) {
      if (onError) onError(err);
    }
  }, [
    src,
    playbackRate,
    autoPlay,
    loop,
    width,
    height,
    onLoad,
    onPlay,
    onPause,
    onStop,
    onEnd,
    onError,
  ]);

  useEffect(() => {
    if (canvasRef.current && !isLoaded) {
      loadAPNG().catch((_) => {
        // empty
      });
    }
  }, [isLoaded, loadAPNG]);

  return (
    <Box
      className={className}
      position={'relative'}
      style={style}
      // borderRadius={'50%'}
      // overflow={'hidden'}
      // boxShadow={'0px 0px 10px #2ca088'}
    >
      <canvas ref={canvasRef} style={{ width: style.width, height: style.height }} />
      <Box position={'absolute'} top={'50%'} left={'50%'} transform={'translate(-50%, -50%)'}>
        {children}
      </Box>
    </Box>
  );
};
